/**
 * Deposit rails — the minimum deposit and confirmation depth on every (currency, network)
 * pair.
 *
 * A coin is not one thing. USDT on Tron and USDT on Ethereum are the same *balance* and
 * completely different *rails*, with different fees and different reorg risk, so the two
 * numbers on this page are set per rail rather than per asset.
 *
 * ## What is editable here, and what is deliberately not
 *
 * Only two figures. Everything else the shared catalogue declares — the address shape, the
 * custodian's ticker for the pair, the chip a customer taps — is a fact about a chain, and a
 * console able to contradict those would be a console able to send a customer's coin to a
 * chain nobody is watching. `minWithdrawal` is absent for a related reason: it exists to stop
 * a send-out whose network fee exceeds the amount, and an operator who set it under the fee
 * would be authorising sends that destroy the customer's money in gas.
 *
 * ## The three-source rule, which is the whole page
 *
 * Every number in force comes from one of three places, and each row says which:
 *
 * - **Catalogue** — the shipped default. A rail nobody has an opinion about tracks it, so
 *   raising Bitcoin's confirmation requirement upstream moves this deployment too.
 * - **Custodian** — NOWPayments' own floor for the ticker, when it can be read. It beats the
 *   catalogue because they hold the coin: a minimum under theirs is a promise we cannot
 *   keep, since a deposit below their floor never reaches us to be credited at all.
 * - **Operator** — what someone typed here. It beats both, because a human made a decision
 *   and a console that quietly overrules it is worse than one that shows them what they did.
 *
 * **Clearing an override is not the same as typing today's catalogue figure**, and the
 * editor is built so an operator cannot confuse the two: "Use the built-in value" removes the
 * override and the rail follows the shipped value forever after, while typing `0.0001`
 * freezes it at `0.0001` even after the catalogue moves. That distinction is invisible for
 * months and then matters on exactly the day a chain's security assumptions change.
 *
 * Read on `rates:read` — every role holds it, and support answering "why was my ₦4,000 of
 * Bitcoin never credited?" needs to see the threshold that refused it. Writes need
 * `rates:write`; a minimum deposit is a treasury call, the same species as a rate override.
 */
import { useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type {
  AdminAssetRail,
  AdminAssetRailsResponse,
  AssetValueSource,
  SetAssetOverrideRequest,
} from '../contracts/types.js';
import { Button, Card, ErrorBox, Input, LoadingState } from '../components/ui.js';
import { IconAlert } from '../components/icons.js';
import { Modal } from '../components/Modal.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { relativeTime } from '../lib/format.js';

const SOURCE_LABELS: Readonly<Record<AssetValueSource, string>> = {
  catalogue: 'Catalogue',
  custodian: 'Custodian',
  operator: 'Operator',
};

export function AssetRailsPage(): JSX.Element {
  const { api, session } = useAuth();
  const canWrite = session !== null && adminCan(session.me.role, 'rates:write');
  const state = useAsync(() => api.listAssetRails(), []);
  const [editing, setEditing] = useState<AdminAssetRail | null>(null);

  if (state.loading) return <LoadingState label="Loading deposit networks…" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;
  if (state.data === null) return <ErrorBox message="No deposit networks are available." />;

  const data: AdminAssetRailsResponse = state.data;
  const byCurrency = groupByCurrency(data.rails);

  return (
    <>
      <div className="page-head">
        {/* The same words the sidebar entry and the topbar use. It said "Deposit rails" long
            after both were renamed, and a heading that disagrees with the link you followed is
            worse than a technical one: the reader cannot tell whether they arrived. */}
        <h1 className="page-head__title">Deposit networks</h1>
        <p className="page-head__sub">
          Every network customers can deposit on. You control two numbers: the smallest deposit that gets credited, and how many confirmations it waits for before the money can be spent.
          {!canWrite && ' Your role can view these but not change them.'}
        </p>
      </div>

      {!data.custodianMinimumsRead && (
        <div className="pcred-note" role="note">
          <IconAlert size={18} />
          <div>
            <strong>We could not reach your crypto provider to read its own minimums.</strong> NOWPayments
            publishes a floor per ticker, and where it is higher than yours a deposit under it
            never reaches us to be credited at all. Nothing below is wrong. Their figure is
            simply missing from the rows rather than known to be absent.
          </div>
        </div>
      )}

      {byCurrency.map((group) => (
        <Card
          key={group.currency}
          title={group.currency}
          actions={
            <span className="faint" style={{ fontSize: 12 }}>
              {group.rails.length === 1 ? '1 network' : `${String(group.rails.length)} networks`}
            </span>
          }
          flush
        >
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Network</th>
                  <th>Minimum deposit</th>
                  <th>Confirmations</th>
                  <th>Last changed</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {group.rails.map((row) => (
                  <tr key={`${row.rail.currency}:${row.rail.network}`}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{row.rail.network}</div>
                      <div className="faint" style={{ fontSize: 11.5 }}>
                        {row.rail.short}
                      </div>
                    </td>
                    <td>
                      <ValueCell
                        value={`${row.effectiveMinDeposit} ${row.rail.currency}`}
                        source={row.minDepositSource}
                        catalogue={`${row.rail.minDeposit} ${row.rail.currency}`}
                        custodian={
                          row.custodianMinDeposit === null
                            ? null
                            : `${row.custodianMinDeposit} ${row.rail.currency}`
                        }
                      />
                    </td>
                    <td>
                      <ValueCell
                        value={String(row.effectiveConfirmations)}
                        source={row.confirmationsSource}
                        catalogue={String(row.rail.confirmationsRequired)}
                        custodian={null}
                      />
                    </td>
                    <td className="faint" style={{ fontSize: 12 }}>
                      {row.updatedAt === null ? 'Never' : relativeTime(row.updatedAt)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>
                        {canWrite ? 'Edit' : 'Details'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {editing !== null && (
        <RailEditor
          // Keyed on the rail so switching rows resets the drafts rather than carrying one
          // rail's half-typed figure onto another.
          key={`${editing.rail.currency}:${editing.rail.network}`}
          row={editing}
          canWrite={canWrite}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setEditing(null);
            state.setData((prev) =>
              prev === null
                ? prev
                : {
                    ...prev,
                    rails: prev.rails.map((r) =>
                      r.rail.currency === updated.rail.currency && r.rail.network === updated.rail.network
                        ? // The write path does not spend a custodian round-trip, so the
                          // figure already on screen is kept rather than blanked.
                          { ...updated, custodianMinDeposit: r.custodianMinDeposit }
                        : r,
                    ),
                  },
            );
          }}
        />
      )}
    </>
  );
}

/** One number, what it is, and where it came from. */
function ValueCell({
  value,
  source,
  catalogue,
  custodian,
}: {
  value: string;
  source: AssetValueSource;
  catalogue: string;
  custodian: string | null;
}): JSX.Element {
  return (
    <div>
      <span className="mono" style={{ fontWeight: 600 }}>
        {value}
      </span>{' '}
      <span className={`rail-src rail-src--${source}`}>{SOURCE_LABELS[source]}</span>
      {source !== 'catalogue' && (
        <div className="faint" style={{ fontSize: 11.5, marginTop: 2 }}>
          catalogue {catalogue}
          {custodian !== null && `, custodian ${custodian}`}
        </div>
      )}
    </div>
  );
}

/**
 * The focused editor for one rail.
 *
 * Each field has three states and the panel keeps them visibly distinct: tracking the
 * catalogue, overridden to a value, or being edited. "Use the built-in value" is a separate
 * control rather than "clear the box", because an empty box is ambiguous — it could mean
 * "remove the override" or "I have not typed it yet" — and the two do opposite things.
 */
function RailEditor({
  row,
  canWrite,
  onClose,
  onSaved,
}: {
  row: AdminAssetRail;
  canWrite: boolean;
  onClose: () => void;
  onSaved: (updated: AdminAssetRail) => void;
}): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const [minDeposit, setMinDeposit] = useState(row.overrideMinDeposit ?? '');
  const [confirmations, setConfirmations] = useState(
    row.overrideConfirmations === null ? '' : String(row.overrideConfirmations),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const minTrimmed = minDeposit.trim();
  const confTrimmed = confirmations.trim();

  const minBad = minTrimmed.length > 0 && !/^\d{1,20}(\.\d{1,18})?$/.test(minTrimmed);
  const confBad =
    confTrimmed.length > 0 && (!/^\d{1,4}$/.test(confTrimmed) || Number(confTrimmed) > 1000);
  const blocked = minBad || confBad;

  // Below the custodian's own floor, an operator is promising to credit a deposit that will
  // never arrive. Not blocked — it is their call, and there are reasons to want it — but it
  // is the one mistake on this page that a customer feels rather than the house.
  const underCustodian =
    row.custodianMinDeposit !== null &&
    minTrimmed.length > 0 &&
    !minBad &&
    compareDecimal(minTrimmed, row.custodianMinDeposit) < 0;

  const changed =
    minTrimmed !== (row.overrideMinDeposit ?? '') ||
    confTrimmed !== (row.overrideConfirmations === null ? '' : String(row.overrideConfirmations));

  async function save(): Promise<void> {
    if (blocked) return;
    setSaving(true);
    setError(null);
    try {
      // Explicit `null` clears the override; a value sets it. Both legs are always sent
      // because the panel shows both and the operator has decided about both — a sparse
      // patch here would make "I emptied this box" indistinguishable from "I did not look
      // at it", and those mean opposite things.
      const body: SetAssetOverrideRequest = {
        minDeposit: minTrimmed.length === 0 ? null : minTrimmed,
        confirmationsRequired: confTrimmed.length === 0 ? null : Number(confTrimmed),
      };
      const updated = await api.setAssetOverride(row.rail.currency, row.rail.network, body);
      toast.success(
        `${row.rail.currency} on ${row.rail.network} saved`,
        'In force on the next deposit detected on this rail.',
      );
      onSaved(updated);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      size="wide"
      title={`${row.rail.currency} on ${row.rail.network}`}
      description={`Custodian ticker ${row.rail.custodyTicker}. Only the two figures below are editable; everything else about this rail is a fact about the chain.`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Close
          </Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={!canWrite || !changed || blocked}
            onClick={() => void save()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="rail-edit">
        <div className="rail-edit__field">
          <label className="field__label" htmlFor="rail-min">
            Minimum deposit
          </label>
          <p className="rail-edit__hint">
            Below this a deposit is not credited. In {row.rail.currency}, as a decimal. The
            catalogue ships <span className="mono">{row.rail.minDeposit}</span>
            {row.custodianMinDeposit !== null && (
              <>
                {' '}
                and the custodian reports <span className="mono">{row.custodianMinDeposit}</span>
              </>
            )}
            .
          </p>
          <Input
            id="rail-min"
            className="mono"
            inputMode="decimal"
            placeholder={`Tracking the catalogue (${row.rail.minDeposit})`}
            value={minDeposit}
            disabled={!canWrite || saving}
            onChange={(e) => setMinDeposit(e.target.value)}
          />
          <FieldFoot
            bad={minBad ? 'Not a decimal amount.' : null}
            overridden={minTrimmed.length > 0}
            disabled={!canWrite || saving}
            onTrack={() => setMinDeposit('')}
          />
        </div>

        <div className="rail-edit__field">
          <label className="field__label" htmlFor="rail-conf">
            Confirmations required
          </label>
          <p className="rail-edit__hint">
            How deeply a deposit is buried before it can be spent. Raising it costs a customer
            minutes; lowering it costs the house a reorg. The catalogue ships{' '}
            <span className="mono">{row.rail.confirmationsRequired}</span>.
          </p>
          <Input
            id="rail-conf"
            className="mono"
            inputMode="numeric"
            placeholder={`Tracking the catalogue (${String(row.rail.confirmationsRequired)})`}
            value={confirmations}
            disabled={!canWrite || saving}
            onChange={(e) => setConfirmations(e.target.value)}
          />
          <FieldFoot
            bad={confBad ? 'A whole number between 0 and 1000.' : null}
            overridden={confTrimmed.length > 0}
            disabled={!canWrite || saving}
            onTrack={() => setConfirmations('')}
          />
        </div>

        {underCustodian && (
          <div className="pcred-warn" role="alert">
            <IconAlert size={16} />
            <div>
              This is below the custodian’s own floor of {row.custodianMinDeposit}. A deposit
              between the two never reaches us, so it can be neither credited nor refunded.
            </div>
          </div>
        )}

        {error !== null && <ErrorBox message={error} />}

        <div className="rail-edit__meta faint">
          {row.updatedAt === null ? (
            <>You have never changed this. It is using the built-in value.</>
          ) : (
            <span title={row.updatedByAdminId ?? undefined}>
              Last changed {relativeTime(row.updatedAt)}
              {row.updatedByAdminId !== null && ` by admin …${row.updatedByAdminId.slice(-6)}`}.
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
}

function FieldFoot({
  bad,
  overridden,
  disabled,
  onTrack,
}: {
  bad: string | null;
  overridden: boolean;
  disabled: boolean;
  onTrack: () => void;
}): JSX.Element {
  return (
    <div className="rail-edit__foot">
      <span className={bad === null ? 'faint' : 'rail-edit__bad'}>
        {bad ??
          (overridden
            ? 'Overridden. This number stays put even if the catalogue moves.'
            : 'Tracking the catalogue. It follows the shipped value.')}
      </span>
      {overridden && (
        <button type="button" className="pcred-linkbtn" disabled={disabled} onClick={onTrack}>
          Use the built-in value
        </button>
      )}
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────────────

interface CurrencyGroup {
  readonly currency: string;
  readonly rails: AdminAssetRail[];
}

/** Group the rails by asset, in the catalogue's own order — the order a customer sees. */
function groupByCurrency(rails: readonly AdminAssetRail[]): CurrencyGroup[] {
  const groups = new Map<string, AdminAssetRail[]>();
  for (const row of rails) {
    const existing = groups.get(row.rail.currency);
    if (existing === undefined) groups.set(row.rail.currency, [row]);
    else existing.push(row);
  }
  return [...groups.entries()].map(([currency, group]) => ({ currency, rails: group }));
}

/**
 * Compare two non-negative decimal strings. −1, 0 or 1.
 *
 * String arithmetic rather than `Number`, for the same reason nothing else here parses an
 * amount: `0.1 + 0.2` is not `0.3`, and a satoshi-scale minimum compared as a float is a
 * comparison that can come out backwards. Only used to decide whether to show a warning, but
 * a warning that fires on the wrong side of the line is worse than no warning at all.
 */
function compareDecimal(left: string, right: string): number {
  const [leftWhole = '0', leftFraction = ''] = left.split('.');
  const [rightWhole = '0', rightFraction = ''] = right.split('.');

  const width = Math.max(leftWhole.length, rightWhole.length);
  const wholeCompare = leftWhole.padStart(width, '0').localeCompare(rightWhole.padStart(width, '0'));
  if (wholeCompare !== 0) return wholeCompare < 0 ? -1 : 1;

  const places = Math.max(leftFraction.length, rightFraction.length);
  const fractionCompare = leftFraction
    .padEnd(places, '0')
    .localeCompare(rightFraction.padEnd(places, '0'));
  return fractionCompare === 0 ? 0 : fractionCompare < 0 ? -1 : 1;
}
